import { z } from "zod";

/**
 * DERS BAZLI HEDEF NET — F-EXM-09 (Emre kararı 07.10.2026)
 *
 * ================================================================
 * KARAR
 * ================================================================
 * Hedef neti KOÇ girer; öğrenci ve veli GÖRÜR ama DEĞİŞTİREMEZ. Düzey SINAV TÜRÜ + DERS'tir (TYT Türkçe, AYT Fizik,
 * LGS Matematik…). Toplam hedef kullanıcıdan alınmaz: DERSLERİN TOPLAMIDIR. Ayrı bir "toplam" alanı olsaydı derslerle
 * çelişebilir ve trend grafiğinde hangisinin çizileceği belirsiz kalırdı.
 *
 * ================================================================
 * NEDEN `Student.targetNets` (JSON) DEĞİL
 * ================================================================
 * Eski JSON sütunu hiçbir ekranda yazılmıyordu ve içindeki `lessonId`'nin dersin silinmesine karşı bir bağı yoktu.
 * Ayrı tablo (`StudentLessonTarget`): ders yabancı anahtarlı, sınav türü ile ders benzersiz, her satır kayıt bazlı
 * yetki kontrolünden geçer. Eski sütun bu işte okunmaz, yazılmaz.
 *
 * Bu dosya SUNUCU ve iki arayüz için tek sözleşmedir; ekranlar toplamı ya da farkı KENDİSİ hesaplamaz, sunucudan alır.
 */

/** Ders başına hedef net üst sınırı (en uzun bölüm `BOLUM_SORU_EN_COK` = 500 soru; net bunu aşamaz). */
export const HEDEF_NET_EN_COK = 500;

/** Bir sınav türü için en çok ders sayısı (TYT/AYT'de 4–5 bölüm; pay bırakıldı). */
export const HEDEF_NET_DERS_EN_COK = 40;

export const HEDEF_NET_SINAV_TURLERI = [
  "TYT",
  "AYT",
  "YDT",
  "LGS",
  "BRANCH",
  "KPSS",
  "DGS",
  "OTHER",
] as const;

export type HedefNetSinavTuru = (typeof HEDEF_NET_SINAV_TURLERI)[number];

export const hedefNetDersSemasi = z.object({
  lessonId: z.string().min(1),
  /** 0 geçerlidir ("bu dersten net beklenmiyor"); eksi ve sonsuz değer reddedilir. */
  targetNet: z.number().finite().min(0).max(HEDEF_NET_EN_COK),
});

/**
 * Koçun gönderdiği: bir sınav türünün TÜM ders hedefleri (tam küme). Küme tek işlemde yazılır; gönderilmeyen ders
 * kümeden çıkar. Boş dizi o sınav türünün hedefini temizler.
 */
export const hedefNetGirdisiSemasi = z
  .object({
    examKind: z.enum(HEDEF_NET_SINAV_TURLERI),
    /**
     * Koçun GÖRDÜĞÜ sürüm (rapordaki `beklenenSurum` kalıbı, F-REP-03a). Küme yazılırken sunucudaki sürümle
     * uyuşmazsa 409 `HEDEF_NET_DEGISTI`: iki koç aynı anda yazarsa sonra gelen sessizce ezmez, güncel hâli yükleyip
     * yeniden karar verir. `null` = "bu sınav türünde hiç hedef YOKTU" (ilk yazım); o sırada başkası yazdıysa yine 409.
     */
    beklenenSurum: z.string().datetime().nullable(),
    hedefler: z
      .array(hedefNetDersSemasi)
      .max(HEDEF_NET_DERS_EN_COK)
      .refine((d) => new Set(d.map((x) => x.lessonId)).size === d.length, {
        message: "Aynı ders iki kez yazılamaz.",
      }),
  })
  .strict();

export type HedefNetGirdisi = z.infer<typeof hedefNetGirdisiSemasi>;

export interface HedefNetDersi {
  lessonId: string;
  ders: string;
  hedefNet: number;
}

/** Bir sınav türünün hedef kümesi. `toplam` derslerin toplamıdır (sunucu hesaplar). */
export interface HedefNetKumesi {
  examKind: HedefNetSinavTuru;
  dersler: HedefNetDersi[];
  toplam: number;
  /**
   * Kümenin sürümü (ISO an): bu sınav türünün TÜM satırlarının (arşivliler dahil) en yeni `updatedAt`'ı. Hiç satır
   * yoksa `null`. İstemci bunu `beklenenSurum` olarak geri gönderir.
   */
  surum: string | null;
}

export interface HedefNetYaniti {
  ogrenciId: string;
  kumeler: HedefNetKumesi[];
}

/** Trend satırında bir dersin hedefle farkı. `fark = net − hedef` (eksi: hedefin altında). */
export interface DenemeDersHedefFarki {
  lessonId: string;
  ders: string;
  /** Sonuçta o ders ölçülmediyse `null` — "0 net" DEĞİL. */
  net: number | null;
  hedef: number;
  fark: number | null;
}

/**
 * Bir deneme satırının hedefle ilişkisi. Hedef girilmemişse satırda `hedefNet` HİÇ yoktur ya da `null`dır
 * ("hedef yok" ile "hedef 0" ayrı şeylerdir).
 */
export interface DenemeHedefFarki {
  /** O sınav türünün hedef toplamı (derslerin toplamı). */
  toplamHedef: number;
  /** `totalNet − toplamHedef`. */
  toplamFark: number;
  /** Yalnız o denemede ölçülen ve hedefi olan dersler; hedefi olup ölçülmeyen ders `net: null` ile gelir. */
  dersler: DenemeDersHedefFarki[];
}
